import useTitanApp from "../../../hooks/useTitanApp";
import { useState, useEffect } from "react";
import {
  addPatientMedications,
  getPatientMedications,
  setShowAddPatientMedicationModal,
  setCurrentPatientMedicationsToEdit,
} from "../../../dispatchers/patientDispatchers";
import { Modal } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import { useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomSelectionDropdown from "../../controls/CustomSelectionDropdown";
import SearchableDropdown from "../../../components/AddPrescriptionItem/SearchableDropdown";
import {
  getDrugBySearch,
  setselectedDrugs,
  setDrugSummary,
} from "../../../dispatchers/nmsDispatchers";
import { LuPill } from "react-icons/lu";

export default function PatientAddMedicationsModal() {
  const { appState, patientState, pharmacyServiceState, nmsServiceState } =
    useTitanApp();
  const [doseValue, setDoseValue] = useState("");
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const { darkMode } = appState || {};
  const { doseShortCodeList } = pharmacyServiceState || {};
  const {
    selectedPatientDetails,
    showAddMedicationsModal,
    addPatientMedicationsLoader,
    selectedPatientMedications,
    isMedicationsEditMode,
    consultationId
  } = patientState || {};
  const formikRef = useRef();

  const { drugSummary } = nmsServiceState || {};
  const closeModal = () => {
    setShowAddPatientMedicationModal(false);
    if (isMedicationsEditMode) {
      setCurrentPatientMedicationsToEdit(null);
    }
  };

  const initialValues = {
    ItemName: selectedPatientMedications?.itemName || "",
    Dose: selectedPatientMedications?.dose || "",
    Quantity: selectedPatientMedications?.quantity || "",
    Measure: selectedPatientMedications
      ? selectedPatientMedications?.measure?.toString() || ""
      : "",
    TherapyLevel: selectedPatientMedications
      ? selectedPatientMedications?.therapyLevel?.toString() || ""
      : "",
    AdditionalNotes: selectedPatientMedications
      ? selectedPatientMedications?.additionalNotes
      : "",
  };

  const validationSchema = Yup.lazy(() => {
    return Yup.object({
      ItemName: Yup.string().required("Required"),
      Dose: Yup.string().required("Required"),
      TherapyLevel: Yup.string().required("Required"),
      AdditionalNotes: Yup.string().nullable(), // Optional
      Quantity: Yup.string().nullable(), // Optional
      Measure: Yup.string().nullable(), // Optional
    });
  });
  const statusOptions = [
    { value: "2", label: "Acute" },
    { value: "1", label: "Chronic" },
    { value: "3", label: "Maintenance" },
    { value: "4", label: "As Needed" },
  ];
  const measureOptions = [
    { value: "1", label: "Tablets" },
    { value: "2", label: "Capsules" },
    { value: "3", label: "ML" },
    { value: "4", label: "MG" },
    { value: "5", label: "G" },
    { value: "6", label: "Drops" },
    { value: "7", label: "Puffs" },
    { value: "8", label: "Units" },
  ];
  const handleAddClick = async (values) => {
    setButtonsDisabled(true);
    let medications = [];
    const patientMedication = {
      itemName: values.ItemName,
      dose: values.Dose,
      quantity: values.Quantity.toString(),
      measure: values.Measure === "" ? null : Number(values.Measure),
      therapyLevel:
        values.TherapyLevel === "" ? null : Number(values.TherapyLevel),
      additionalNotes: values.AdditionalNotes,
    };

    if (isMedicationsEditMode) {
      medications.push(selectedPatientMedications);
    }
    medications.push(patientMedication);
    const patientMedications = {
      consultationId,
      patientId: selectedPatientDetails?.patientID,
      medications: medications,
    };
    await addPatientMedications(patientMedications);
    await getPatientMedications(selectedPatientDetails?.patientID);
    setCurrentPatientMedicationsToEdit(null);
    closeModal();
  };

  const handleDoseChange = (e, setFieldValue) => {
    const inputValue = e.target.value;
    const endsWithSpace = inputValue?.endsWith(" ");
    if (endsWithSpace) {
      const words = inputValue.trim().split(" ");
      const lastWord = words[words.length - 1];
      const matched = doseShortCodeList?.find(
        (item) => item?.shortCode?.toLowerCase() === lastWord.toLowerCase()
      );
      if (matched) {
        words[words.length - 1] = matched.description;
        const updatedValue = words.join(" ") + " ";
        setDoseValue(updatedValue);
        setFieldValue("Dose", updatedValue);
        return;
      }
    }
    setDoseValue(inputValue);
    setFieldValue("Dose", inputValue);
  };

  useEffect(() => {
    if (showAddMedicationsModal && isMedicationsEditMode) {
      const fetchAndSelectDrug = async () => {
        const response = await getDrugBySearch({
          Keyword: selectedPatientMedications?.itemName,
        });
        if (Array.isArray(response) && response.length > 0) {
          setselectedDrugs(response);
          setDrugSummary(response);
          const drugName =
            typeof response[0].drugName === "string"
              ? response[0].drugName.trim()
              : "";

          // Use setTimeout to ensure Formik is ready and the drugSummary state has been updated
          setTimeout(() => {
            if (formikRef.current) {
              formikRef.current.setFieldValue("ItemName", drugName);
            }
          }, 100);
        }
      };
      fetchAndSelectDrug();
    }
  }, [showAddMedicationsModal, isMedicationsEditMode]);

  return (
    <Modal
      show={showAddMedicationsModal}
      onHide={closeModal}
      centered
      backdrop="static"
      dialogClassName="pc-modal"
      size="modal"
    >
      <div className="header">
        <div className="info pc-add-modal-header-info">
          <div>
            <LuPill color={darkMode ? "#bdbdbd" : "#0a0a0a"} />
          </div>
          <p className="label">
            {isMedicationsEditMode ? "Edit" : "Add"} Patient Medication
          </p>
          <p className="desc"></p>
        </div>
        <IoClose
          size={20}
          color={darkMode ? "#bdbdbd" : "#7c7c7c"}
          onClick={closeModal}
        />
      </div>

      <div className="body" id="bodyID">
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleAddClick}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form>
              <div className="fields-wrapper">
                <div className="form-group add_medication_search">
                  <label>Medicine Name *</label>
                  <SearchableDropdown
                    className={`searchable-dropdown ${
                      errors.value ? "error" : ""
                    }`}
                    searchType="drug"
                    placeholder="e.g., Amlodipine 10mg tablets"
                    label="name"
                    id="id"
                    selectedVal={values.ItemName}
                    handleChange={(val) => {
                      if (val && typeof val === "object") {
                        setFieldValue("ItemName", val?.drugName?.trim() ?? "");
                      } else {
                        setFieldValue("ItemName", val);
                      }
                    }}
                    options={Array.isArray(drugSummary) ? drugSummary : []}
                  />
                  {/* <input
                    type="text"
                    name="ItemName"
                    value={values.ItemName}
                    onChange={handleChange}
                    placeholder="e.g., Amlodipine 10mg tablets"
                    autoComplete="off"
                    autoFocus
                  /> */}
                  {touched.ItemName && errors.ItemName && (
                    <p className="field-error">{errors.ItemName}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Dose *</label>
                  <input
                    type="text"
                    name="Dose"
                    value={values.Dose}
                    // onChange={handleChange}
                    onChange={(e) => handleDoseChange(e, setFieldValue)}
                    placeholder="e.g., Take One daily"
                    autoComplete="off"
                  />
                  {touched.Dose && errors.Dose && (
                    <p className="field-error">{errors.Dose}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    name="Quantity"
                    value={values.Quantity}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "" || parseInt(value) > 0) {
                        handleChange(e);
                      }
                    }}
                    min="1"
                    onKeyDown={(e) => {
                      // Block negative sign, decimal point, and scientific notation (e/E)
                      if (
                        e.key === "." ||
                        e.key === "," ||
                        e.key.toLowerCase() === "e" ||
                        e.key === "-"
                      ) {
                        e.preventDefault();
                      }

                      if (
                        e.key === "ArrowDown" &&
                        (!values.Quantity || parseInt(values.Quantity) <= 1)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="4"
                    autoComplete="off"
                  />
                </div>
              
                <div className="form-group">
                  <CustomSelectionDropdown
                    label="Therapy *"
                    name="TherapyLevel"
                    value={values.TherapyLevel}
                    options={statusOptions}
                    onChange={handleChange}
                  />
                  {touched.TherapyLevel && errors.TherapyLevel && (
                    <p className="field-error">{errors.TherapyLevel}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Additional Notes</label>
                  <input
                    className="template_input text_input_field"
                    type="text"
                    name="AdditionalNotes"
                    value={values.AdditionalNotes}
                    onChange={handleChange}
                    autoComplete="off"
                    placeholder="Any additional information about this medication.."
                  />
                </div>
              </div>

              <div className="footer-actions">
                <button className="cancel" type="button" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="done"
                  type="submit"
                  disabled={buttonsDisabled}
                >
                  {isMedicationsEditMode
                    ? addPatientMedicationsLoader
                      ? "Updating Medication..."
                      : "Update Medication"
                    : addPatientMedicationsLoader
                    ? "Adding Medication..."
                    : "Add Medication"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
